/**
 * LetterViewer — the payoff (design-spec/05 Screen 04). It must read like a credible signed
 * document, not an app screen: the Gelasio letter body (the bundled cross-surface letter face),
 * the Caveat signature, the referee's printed name / role / company / signed date, and the gold
 * verified seal with its reference id. The render is intentionally identical across app / referee /
 * employer surfaces — that consistency is the trust guarantee.
 *
 * Attestation language stays honest ("contact confirmed" only at MVP — no identity claim). The seal
 * carries the reference id and designs the slot for a future public verify URL (content hash).
 */
import type { ReferenceContent } from '@/lib/letter-content';

function formatSignedDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

const ShieldCheck = (
  <svg viewBox="0 0 24 24" fill="none" width={13} height={13} aria-hidden="true">
    <path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface LetterViewerProps {
  content: ReferenceContent;
  onExportPdf: () => void;
  onShare: () => void;
  pdfPending?: boolean;
  /** A genuine PDF failure (coral alert) — e.g. the open failed. */
  pdfError?: string | null;
  /** A calm, non-error PDF notice (info tone) — e.g. "being prepared, try again" while it renders. */
  pdfNotice?: string | null;
  /**
   * Play the unlock ceremony (design-spec/03 §6 + Screen 04): the letter card rises and the gold
   * seal stamps in (`back.out(1.7)` overshoot, ~450ms). Set for one render right after a successful
   * unlock; stilled entirely under prefers-reduced-motion (index.css). Purely visual — the reveal is
   * announced to assistive tech by the detail screen's live region, not this decorative seal.
   */
  sealing?: boolean;
}

export function LetterViewer({
  content,
  onExportPdf,
  onShare,
  pdfPending,
  pdfError,
  pdfNotice,
  sealing,
}: LetterViewerProps) {
  const letter = content.letter;
  if (!letter) return null;
  const metaTop = [content.refereeRole, content.company].filter(Boolean).join(' · ');
  const roleLine = [content.refereeRole, content.company, formatSignedDate(content.signedDate)]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-1 flex-col">
      <div className="mb-3 flex items-center justify-between">
        <span
          className="inline-flex items-center gap-1.5 rounded-pill bg-sage-tint px-2.5 py-1 font-display text-[11px] font-semibold text-sage-text"
          title="We confirmed contact with this referee. We do not verify identity at MVP."
        >
          {ShieldCheck}
          Verified
        </span>
      </div>

      <article
        className={`relative overflow-hidden rounded-xl border border-line bg-surface p-5 shadow-sm${
          sealing ? ' unlock-reveal' : ''
        }`}
      >
        <p className="mb-3.5 font-display text-[10.5px] font-semibold uppercase tracking-[0.04em] text-ink-quiet">
          {metaTop}
        </p>
        {letter.salutation && (
          <p className="font-letter text-[14px] leading-relaxed text-ink-quiet">{letter.salutation}</p>
        )}
        {letter.paragraphs.map((para, i) => (
          <p key={i} className="mt-3 font-letter text-[14px] leading-[1.72] text-ink">
            {para}
          </p>
        ))}

        <p className="mt-4 pr-16 font-hand text-[34px] leading-none text-ink">{letter.signatureName}</p>
        <p className="mt-1.5 font-display text-[12.5px] font-semibold text-ink">{letter.signatureName}</p>
        {roleLine && <p className="mt-0.5 pr-16 font-body text-[11px] text-ink-quiet">{roleLine}</p>}

        <span
          aria-hidden="true"
          className={`absolute bottom-4 right-4 grid h-[62px] w-[62px] place-items-center rounded-full border-2 border-[rgba(255,244,214,0.5)] bg-[radial-gradient(circle_at_34%_30%,#E7B45C,#C8892A_55%,#A76E1E)] text-center text-[#fff3d8] shadow-[0_10px_20px_-8px_rgba(168,110,30,0.75),inset_0_1px_5px_rgba(255,255,255,0.4)]${
            sealing ? ' seal-stamp' : ''
          }`}
        >
          <span className="flex flex-col items-center">
            {ShieldCheck}
            <span className="mt-0.5 font-display text-[6.5px] font-bold uppercase tracking-[0.09em] leading-tight">
              Verified
            </span>
          </span>
        </span>
      </article>

      <p className="mt-2.5 text-center font-display text-[10.5px] tracking-[0.03em] text-ink-quiet">
        Verified by By My Word · Ref {content.referenceId}
      </p>

      {pdfError && (
        <p role="alert" className="mt-2 rounded-md bg-coral-tint px-3 py-2 text-center font-body text-[12px] text-coral-text">
          {pdfError}
        </p>
      )}
      {!pdfError && pdfNotice && (
        <p role="status" className="mt-2 rounded-md bg-surface-tint px-3 py-2 text-center font-body text-[12px] text-ink">
          {pdfNotice}
        </p>
      )}

      <div className="mt-3.5 flex gap-2.5">
        <button
          type="button"
          onClick={onExportPdf}
          disabled={pdfPending}
          className="flex h-12 flex-1 items-center justify-center rounded-md bg-sage-deep font-display text-[14px] font-semibold text-white shadow-btn-sage transition-colors duration-ui ease-thread hover:bg-sage-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:opacity-50"
        >
          {pdfPending ? 'Preparing…' : 'Export PDF'}
        </button>
        <button
          type="button"
          onClick={onShare}
          className="flex h-12 flex-1 items-center justify-center rounded-md border border-line-2 bg-surface font-display text-[14px] font-semibold text-ink transition-colors duration-ui ease-thread hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
        >
          Share
        </button>
      </div>
    </div>
  );
}
